import isEmpty from "lodash/isEmpty";
import type { IProductLean } from "../../pages/ProductsListPage/entities/product-lean/product-lean.entity.interface";
import { GTMAffiliationCollection } from "./collection/gtmAffiliationCollection";
import { GTMEventEcommerceCollection } from "./collection/gtmEventCollection";
import { GTMItemListIdCollection } from "./collection/gtmItemListIdCollection";
import type { GTM } from "./GTMInterfaces";
import { sendGtmEventEcommerce } from "./sendGtmEventEcommerce";
import { enrichServiceWithCashbackFromPartnerData } from "./servicesWithCashbackFromPartner/enrichServiceWithCashbackFromPartnerData";
import { splitServicesWithCashbackFromPartner } from "./servicesWithCashbackFromPartner/splitServicesWithCashbackFromPartner";

export function sendGtmEventSelectItem(service: GTM.ServiceExtended): void {
  const itemListId = GTMItemListIdCollection.onlineService;
  const eventName = GTMEventEcommerceCollection.selectItem;
  sendGtmEventEcommerce({ itemListId, services: [service], eventName });
}

export function sendGtmEventSelectItemNew(service: IProductLean): void {
  const itemListId = GTMItemListIdCollection.onlineService;
  const eventName = GTMEventEcommerceCollection.selectItem;
  const gtmServiceExtended: GTM.ServiceExtended = {
    id: service.id,
    available: service.available,
    price: 0,
    shortDescription: service.shortDescription,
    type: service.type,
    name: service.name,
    category: service.category,
    unique_identifier: service.uniqueIdentifier,
    discount: {
      code: "",
      amount: 0,
    },
  } as GTM.ServiceExtended;
  sendGtmEventEcommerce({ itemListId, services: [gtmServiceExtended], eventName });
}

export function sendGtmEventViewItem(product: GTM.ServiceExtended): void {
  const itemListId = GTMItemListIdCollection.onlineService;
  const eventName = GTMEventEcommerceCollection.viewItem;
  sendGtmEventEcommerce({ itemListId, services: [product], eventName });
}

export interface EventPurchaseProps {
  paymentType: string;
  coupon: string;
  value: number;
  userData: Partial<GTM.UserData>;
  currency: string;
  transactionId?: string;
  affiliation: string;
  tax?: number;
}

interface EventPurchaseOperatorProps extends EventPurchaseProps {
  clientId?: string;
  gclid?: string;
}

/** @deprecated Used only for subscription purchases tracking via Chargebee */
export function sendGTMEventPurchase(config: EventPurchaseProps, services: GTM.ServiceExtended[]): void {
  const eventName = GTMEventEcommerceCollection.purchase;
  const itemListId = GTMItemListIdCollection.onlineService;
  sendGtmEventEcommerce({ itemListId, services, eventName, ...config });
}

function sendGTMEventPurchaseOperator(config: EventPurchaseOperatorProps, services: GTM.ServiceExtended[]): void {
  const eventName = GTMEventEcommerceCollection.purchase_operator;
  const itemListId = GTMItemListIdCollection.onlineService;
  sendGtmEventEcommerce({ itemListId, services, eventName, ...config });
}

interface SendGTMAddToWishListEventProps extends SendGTMAddToWishListEvent {
  coupon: string;
  value: number;
  affiliation?: GTMAffiliationCollection;
  currency: string;
  transactionId?: string;
}
export function sendGTMEventAddToWishList(
  config: SendGTMAddToWishListEventProps,
  services: GTM.ServiceExtended[]
): void {
  const eventName = GTMEventEcommerceCollection.addToWishlist;
  const itemListId = GTMItemListIdCollection.onlineService;
  try {
    const servicesWithCashbackFromPartnersData = services.map(enrichServiceWithCashbackFromPartnerData);
    sendGtmEventEcommerce({ itemListId, services: servicesWithCashbackFromPartnersData, eventName, ...config });
  } catch (error) {
    console.error("Failed to enrich services with cashback from partner data");
  }
}

export type SendGTMEventOnPurchaseProps = SendGTMPurchaseEvent & SendGTMAddToWishListEvent;

interface SendGTMPurchaseEvent {
  userData: Partial<GTM.UserData>;
  value: number;
  coupon: string;
  paymentType: string;
  currency: string;
  transactionId?: string;
  tax?: number;
}

interface SendGTMAddToWishListEvent {
  value: number;
  coupon: string;
  clientId?: string;
  gclid?: string;
}

export interface SendGTMRefundEventProps {
  value: number;
  coupon: string;
  paymentType: string;
  currency: string;
  transactionId?: string;
  tax?: number;
}

export function sendGTMEventRefund(config: SendGTMRefundEventProps, services: GTM.ServiceExtended[]) {
  const eventName = GTMEventEcommerceCollection.refund;
  const itemListId = GTMItemListIdCollection.onlineService;
  const affiliation = GTMAffiliationCollection.admin;
  sendGtmEventEcommerce({ itemListId, services, eventName, affiliation, ...config });
}

export type SendGTMEventOnFinalized = {
  clientId?: string;
  gclid?: string;
  userData: Partial<GTM.UserData>;
  currency?: string;
  transactionId: string;
  tax: number;
  value: number;
  coupon?: string;
  paymentType?: string;
};

export function sendGTMEventOnFinalizedPractice(config: SendGTMEventOnFinalized, services: GTM.ServiceExtended[]) {
  const { currency = "EUR", coupon = "", paymentType = "" } = config;

  const [_, servicesWithCashbackFromPartner] = splitServicesWithCashbackFromPartner(services);

  if (isEmpty(servicesWithCashbackFromPartner)) {
    return;
  }

  const affiliation = GTMAffiliationCollection.operator;
  sendGTMEventPurchaseOperator(
    { ...config, currency, coupon, paymentType, affiliation },
    servicesWithCashbackFromPartner
  );
}
